import * as htmlToImage from "html-to-image";
import html2canvas from "html2canvas";
import AWS from "aws-sdk";

const S3_BUCKET = process.env.REACT_APP_PUBLIC_S3_BUCKET_NAME;
const S3_REGION = process.env.REACT_APP_PUBLIC_COGNITO_POOL_REGION;

const ensureAwsConfig = () => {
  if (!AWS.config.region) {
    AWS.config.update({
      region: S3_REGION,
      credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: process.env.REACT_APP_PUBLIC_COGNITO_POOL_ID,
      }),
    });
  }
};

const shouldIncludeNode = (node) => {
  if (!node || !node.tagName) return true;
  const tag = node.tagName.toUpperCase();
  return tag !== "VIDEO" && tag !== "AUDIO" && tag !== "IFRAME";
};

const triggerDownload = (dataUrl) => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `screenshot-${Date.now()}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const bitmapToPngDataUrl = (bitmap) => {
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return canvas.toDataURL("image/png");
};

const bytesToPngDataUrl = async (bytes, contentType) => {
  const blob = new Blob([bytes], {
    type: contentType || "application/octet-stream",
  });
  const bitmap = await createImageBitmap(blob);
  return bitmapToPngDataUrl(bitmap);
};

const isLocalAssetUrl = (urlString) => {
  if (!urlString) return true;
  if (urlString.startsWith("data:") || urlString.startsWith("blob:")) return true;
  if (urlString.includes("/static/")) return true;
  // Bundled SVG/PNG icons used for UI chrome (not user photos)
  if (/\.(svg|png|jpe?g|gif|webp)(\?|$)/i.test(urlString)) {
    try {
      const url = new URL(urlString, window.location.origin);
      if (url.origin === window.location.origin && url.pathname.includes("/static/")) {
        return true;
      }
    } catch (e) {
      // ignore
    }
  }
  return false;
};

/**
 * Resolve bucket/key for user photos hosted on S3 / CloudFront.
 */
const parseS3Target = (urlString) => {
  if (!urlString || isLocalAssetUrl(urlString)) return null;

  try {
    const url = new URL(urlString, window.location.origin);
    const host = url.hostname;

    const virtualHosted = host.match(
      /^(.+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i
    );
    if (virtualHosted) {
      return {
        bucket: virtualHosted[1],
        key: decodeURIComponent(url.pathname.replace(/^\//, "")),
      };
    }

    if (/^s3[.-]/i.test(host) || host === "s3.amazonaws.com") {
      const parts = url.pathname.replace(/^\//, "").split("/");
      if (parts.length >= 2) {
        return {
          bucket: parts[0],
          key: decodeURIComponent(parts.slice(1).join("/")),
        };
      }
    }

    if (S3_BUCKET) {
      // Absolute URL on another host (e.g. CloudFront) → treat pathname as key
      if (url.origin !== window.location.origin) {
        const key = decodeURIComponent(url.pathname.replace(/^\//, ""));
        if (key) return { bucket: S3_BUCKET, key };
      }

      // Key-only path from DB when IMAGE_BASEURL is empty (e.g. "userImages/a.jpg")
      if (!String(urlString).includes("://")) {
        const key = String(urlString).replace(/^\//, "");
        if (key && !key.startsWith("static/")) {
          return { bucket: S3_BUCKET, key };
        }
      }
    }
  } catch (e) {
    if (S3_BUCKET && !String(urlString).includes("://")) {
      const key = String(urlString).replace(/^\//, "");
      if (key && !key.startsWith("static/")) {
        return { bucket: S3_BUCKET, key };
      }
    }
  }

  return null;
};

const bodyToUint8Array = async (body) => {
  if (!body) throw new Error("Empty S3 body");
  if (body instanceof Uint8Array) return body;
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  if (ArrayBuffer.isView(body)) {
    return new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
  }
  if (typeof body === "string") {
    return new TextEncoder().encode(body);
  }
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return new Uint8Array(await body.arrayBuffer());
  }
  if (typeof body === "object" && body.data) {
    return Uint8Array.from(body.data);
  }
  return new Uint8Array(await new Response(body).arrayBuffer());
};

const fetchViaS3 = async (urlString) => {
  ensureAwsConfig();
  const target = parseS3Target(urlString);
  if (!target?.bucket || !target?.key) {
    throw new Error("Not an S3 object");
  }

  const s3 = new AWS.S3({ region: S3_REGION || AWS.config.region });
  const result = await s3
    .getObject({ Bucket: target.bucket, Key: target.key })
    .promise();

  const bytes = await bodyToUint8Array(result.Body);
  return bytesToPngDataUrl(bytes, result.ContentType);
};

const fetchViaHttp = async (url) => {
  const response = await fetch(url, {
    mode: "cors",
    cache: "reload",
    credentials: "omit",
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  return bitmapToPngDataUrl(bitmap);
};

const shouldInlineImage = (url) => {
  if (!url || isLocalAssetUrl(url)) return false;
  if (parseS3Target(url)) return true;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin !== window.location.origin;
  } catch (e) {
    return Boolean(S3_BUCKET);
  }
};

/**
 * Convert a remote photo URL to a PNG data URL.
 * Uses S3 SDK first so we don't need public-object CORS (which broke <img crossOrigin>).
 */
const urlToPngDataUrl = async (url) => {
  if (!url || url.startsWith("data:")) return url;

  const errors = [];

  if (parseS3Target(url)) {
    try {
      return await fetchViaS3(url);
    } catch (error) {
      errors.push(error);
    }
  }

  try {
    return await fetchViaHttp(url);
  } catch (error) {
    errors.push(error);
  }

  console.warn("Could not inline image for screenshot:", url, errors);
  return null;
};

const captureWithHtmlToImage = (element) =>
  htmlToImage.toPng(element, {
    cacheBust: false,
    skipFonts: true,
    pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
    backgroundColor: "#ffffff",
    filter: shouldIncludeNode,
    imagePlaceholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==",
    onImageErrorHandler: () => {},
  });

const captureWithHtml2Canvas = async (element) => {
  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: false,
    logging: false,
    scale: Math.min(window.devicePixelRatio || 1, 2),
    backgroundColor: "#ffffff",
    ignoreElements: (el) => !shouldIncludeNode(el),
  });
  return canvas.toDataURL("image/png");
};

const waitForImageDecode = (img) =>
  new Promise((resolve) => {
    if (img.complete && img.naturalWidth > 0) return resolve();
    img.addEventListener("load", resolve, { once: true });
    img.addEventListener("error", resolve, { once: true });
    // data URLs usually decode synchronously; never hang the capture
    setTimeout(resolve, 2000);
  });

/**
 * Captures a DOM node as a PNG and downloads it.
 *
 * Remote user photos lack CORS headers, so their bytes are pulled via the S3
 * SDK and swapped into the live <img> nodes just for the capture, then restored.
 * The redux screenshot trigger must be reset only AFTER this resolves, otherwise
 * a re-render can remount image nodes mid-capture (leaving blanks).
 */
export async function captureAndDownloadScreenshot(element) {
  if (!element) {
    throw new Error("Screenshot target not found");
  }

  const images = Array.from(element.querySelectorAll("img"));
  const backups = images.map((img) => ({
    img,
    srcAttr: img.getAttribute("src"),
  }));

  try {
    await Promise.all(
      images.map(async (img) => {
        const url = img.getAttribute("src") || img.src;
        if (!shouldInlineImage(url)) return;

        const dataUrl = await urlToPngDataUrl(url);
        if (dataUrl) {
          img.setAttribute("src", dataUrl);
        }
      })
    );

    await Promise.all(images.map(waitForImageDecode));

    try {
      const dataUrl = await captureWithHtmlToImage(element);
      triggerDownload(dataUrl);
      return;
    } catch (primaryError) {
      console.warn(
        "html-to-image capture failed, trying html2canvas:",
        primaryError
      );
    }

    const dataUrl = await captureWithHtml2Canvas(element);
    triggerDownload(dataUrl);
  } finally {
    backups.forEach(({ img, srcAttr }) => {
      if (srcAttr != null) {
        img.setAttribute("src", srcAttr);
      } else {
        img.removeAttribute("src");
      }
    });
  }
}
